import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { ShieldAlert, ChevronRight, AlertTriangle, Check, Clock, DollarSign, ArrowRight, ShoppingCart, Tv, Coffee, PiggyBank } from 'lucide-react';

const REASONS = [
  { key: 'bored', label: "I'm bored" },
  { key: 'stressed', label: "I'm stressed/anxious" },
  { key: 'chase_losses', label: 'I want to win back losses' },
  { key: 'celebrating', label: "I'm celebrating something" },
  { key: 'fun', label: 'Just for fun' },
  { key: 'other', label: 'Other' },
];

export default function RegretAnticipation() {
  const [step, setStep] = useState(1);
  const [depositAmount, setDepositAmount] = useState('');
  const [reason, setReason] = useState('');
  const [reasonDetail, setReasonDetail] = useState('');
  const [reflection, setReflection] = useState(null);
  const [decision, setDecision] = useState(null);
  const [lossLimit, setLossLimit] = useState('');
  const [ackText, setAckText] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/tools/regret-anticipation/history').then(r => setHistory(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  const handleStartReflection = async () => {
    if (!depositAmount || !reason) { toast.error('Fill in all fields'); return; }
    setLoading(true);
    try {
      const res = await api.post('/tools/regret-anticipation/start', {
        deposit_amount: parseFloat(depositAmount), reason, reason_detail: reasonDetail,
      });
      setReflection(res.data);
      setStep(2);
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const handleWait = async () => {
    try {
      const res = await api.post('/tools/regret-anticipation/decide', {
        reflection_id: reflection.reflection_id, chose_to_wait: true,
      });
      setDecision(res.data);
      setStep(6);
    } catch { toast.error('Failed'); }
  };

  const handleProceed = async () => {
    if (!lossLimit) { toast.error('Set a loss limit'); return; }
    if (ackText.toLowerCase() !== 'i understand') { toast.error('Type "I understand" to proceed'); return; }
    try {
      const res = await api.post('/tools/regret-anticipation/decide', {
        reflection_id: reflection.reflection_id, chose_to_wait: false,
        typed_acknowledgment: true, loss_limit_set: parseFloat(lossLimit),
      });
      setDecision(res.data);
      setStep(6);
    } catch { toast.error('Failed'); }
  };

  const reset = () => {
    setStep(1); setDepositAmount(''); setReason(''); setReasonDetail('');
    setReflection(null); setDecision(null); setLossLimit(''); setAckText(''); setCountdown(0);
    api.get('/tools/regret-anticipation/history').then(r => setHistory(r.data)).catch(() => {});
  };

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 py-8 lg:py-12" data-testid="regret-anticipation-page">
      <nav className="flex items-center gap-2 text-sm text-[#64748b] mb-6">
        <Link to="/dashboard" className="hover:text-[#4d7cfe]">Dashboard</Link><ChevronRight className="h-3.5 w-3.5" />
        <span className="text-[#a0a8b8]">Emotional Wellness</span><ChevronRight className="h-3.5 w-3.5" /><span className="text-white">Regret Anticipation</span>
      </nav>
      <div className="flex items-start gap-4 mb-8 animate-fade-in">
        <div className="w-12 h-12 rounded-xl bg-[#8b5cf6]/15 flex items-center justify-center shrink-0"><ShieldAlert className="h-6 w-6 text-[#8b5cf6]" /></div>
        <div><h1 className="text-2xl lg:text-[32px] font-bold text-white">Regret Anticipation Tool</h1><p className="text-[#a0a8b8] text-sm mt-1">Pre-deposit reflection - visualize future outcomes before deciding</p></div>
      </div>

      {/* Step Indicator */}
      {step <= 5 && (
        <div className="flex items-center gap-1 mb-8">
          {[1,2,3,4,5].map(s => (
            <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? 'bg-[#8b5cf6]' : 'bg-[#252b3b]'}`} />
          ))}
        </div>
      )}

      {/* Step 1: Current Situation */}
      {step === 1 && (
        <div className="ciq-card max-w-lg animate-fade-in" data-testid="step-1">
          <h2 className="text-lg font-bold text-white mb-4">Before You Deposit... Let's Think Ahead</h2>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">How much are you about to deposit?</label>
              <div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748b] text-sm">$</span>
                <input type="number" value={depositAmount} onChange={e => setDepositAmount(e.target.value)} required min="1" placeholder="100"
                  className="w-full h-11 pl-8 pr-4 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none" data-testid="regret-amount-input" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Why do you want to gamble right now?</label>
              <div className="space-y-2">
                {REASONS.map(r => (
                  <button key={r.key} onClick={() => setReason(r.key)}
                    className={`w-full text-left p-3 rounded-lg border transition-all text-sm ${reason === r.key ? 'bg-[#8b5cf6]/10 border-[#8b5cf6]/30 text-white' : 'bg-[#1a1f2e] border-white/10 text-[#a0a8b8]'}`}
                    data-testid={`reason-${r.key}`}>{r.label}</button>
                ))}
              </div>
            </div>
            <button onClick={handleStartReflection} disabled={loading || !depositAmount || !reason}
              className="w-full h-12 rounded-xl bg-[#8b5cf6] text-white font-medium text-sm btn-lift disabled:opacity-50" data-testid="regret-start-btn">
              {loading ? 'Processing...' : 'Continue Reflection'}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Future Visualization */}
      {step === 2 && reflection && (
        <div className="animate-slide-up space-y-5" data-testid="step-2">
          <h2 className="text-lg font-bold text-white mb-2">Tomorrow Morning: You Lost ${parseFloat(depositAmount).toLocaleString()}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="ciq-card border-[#ef4444]/20 bg-[#ef4444]/5">
              <p className="text-xs text-[#ef4444] font-semibold uppercase mb-3">If You Deposit & Lose</p>
              <p className="text-sm text-[#a0a8b8] mb-2">{reflection.if_lose_scenario.bank_balance_after}</p>
              <p className="text-xs text-[#64748b] mb-3">How will you feel?</p>
              <div className="space-y-1">
                {reflection.if_lose_scenario.feeling_options.map((f, i) => (
                  <p key={i} className="text-xs text-[#ef4444]/80">- {f}</p>
                ))}
              </div>
            </div>
            <div className="ciq-card border-[#10b981]/20 bg-[#10b981]/5">
              <p className="text-xs text-[#10b981] font-semibold uppercase mb-3">If You Don't Deposit</p>
              <p className="text-sm text-[#a0a8b8] mb-2">{reflection.if_skip_scenario.bank_balance_after}</p>
              <p className="text-xs text-[#64748b] mb-3">How will you feel?</p>
              <div className="space-y-1">
                {reflection.if_skip_scenario.feeling_options.map((f, i) => (
                  <p key={i} className="text-xs text-[#10b981]/80">- {f}</p>
                ))}
              </div>
            </div>
          </div>
          <button onClick={() => setStep(3)} className="w-full h-11 rounded-xl bg-[#8b5cf6] text-white text-sm font-medium btn-lift" data-testid="step2-next-btn">See The Odds</button>
        </div>
      )}

      {/* Step 3: Probability Reality Check */}
      {step === 3 && reflection && (
        <div className="animate-slide-up space-y-5" data-testid="step-3">
          <h2 className="text-lg font-bold text-white">The Odds Are Against You</h2>
          <div className="ciq-card border-[#ef4444]/20">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div><p className="text-xs text-[#64748b]">Chance You'll LOSE</p><p className="text-2xl font-bold text-[#ef4444]">{reflection.probability.lose_chance}%</p></div>
              <div><p className="text-xs text-[#64748b]">Chance You'll WIN</p><p className="text-2xl font-bold text-[#10b981]">{reflection.probability.win_chance}%</p></div>
              <div><p className="text-xs text-[#64748b]">Expected Loss</p><p className="text-2xl font-bold text-[#f59e0b]">${reflection.probability.expected_loss}</p></div>
            </div>
            {/* Visual bar */}
            <div className="h-4 rounded-full overflow-hidden flex mt-4">
              <div className="bg-[#ef4444]" style={{ width: `${reflection.probability.lose_chance}%` }} />
              <div className="bg-[#10b981]" style={{ width: `${reflection.probability.win_chance}%` }} />
            </div>
            <div className="flex justify-between text-[10px] text-[#64748b] mt-1">
              <span>Lose ({reflection.probability.lose_chance}%)</span><span>Win ({reflection.probability.win_chance}%)</span>
            </div>
          </div>
          <button onClick={() => setStep(4)} className="w-full h-11 rounded-xl bg-[#8b5cf6] text-white text-sm font-medium btn-lift" data-testid="step3-next-btn">What Else Could This Money Do?</button>
        </div>
      )}

      {/* Step 4: Alternative Use Calculator */}
      {step === 4 && reflection && (
        <div className="animate-slide-up space-y-5" data-testid="step-4">
          <h2 className="text-lg font-bold text-white">What Else Could ${parseFloat(depositAmount).toLocaleString()} Do?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {reflection.alternatives.map((alt, i) => (
              <div key={i} className="ciq-card py-3 flex items-center gap-3" data-testid={`alt-use-${i}`}>
                <div className="w-8 h-8 rounded-lg bg-[#4d7cfe]/10 flex items-center justify-center shrink-0">
                  <DollarSign className="h-4 w-4 text-[#4d7cfe]" />
                </div>
                <p className="text-sm text-[#a0a8b8]">{alt.text}</p>
              </div>
            ))}
          </div>
          <button onClick={() => setStep(5)} className="w-full h-11 rounded-xl bg-[#8b5cf6] text-white text-sm font-medium btn-lift" data-testid="step4-next-btn">Make Your Decision</button>
        </div>
      )}

      {/* Step 5: Cooling-Off Commitment */}
      {step === 5 && reflection && (
        <div className="animate-slide-up space-y-5" data-testid="step-5">
          <h2 className="text-lg font-bold text-white text-center">Let's Make a Deal</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Wait Option */}
            <div className="ciq-card border-[#10b981]/20 bg-[#10b981]/5 hover:border-[#10b981]/40 transition-all cursor-pointer" onClick={handleWait} data-testid="choose-wait-btn">
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#10b981]/15 flex items-center justify-center mx-auto mb-3"><Clock className="h-6 w-6 text-[#10b981]" /></div>
                <h3 className="text-white font-bold mb-1">I'll Wait 24 Hours</h3>
                <p className="text-xs text-[#a0a8b8]">Recommended</p>
                <p className="text-xs text-[#10b981] mt-2">Protect yourself from potential regret</p>
              </div>
            </div>
            {/* Proceed Option */}
            <div className="ciq-card border-[#ef4444]/20">
              <div className="text-center mb-3">
                <div className="w-12 h-12 rounded-full bg-[#ef4444]/15 flex items-center justify-center mx-auto mb-3"><AlertTriangle className="h-6 w-6 text-[#ef4444]" /></div>
                <h3 className="text-white font-bold mb-1">Deposit Anyway</h3>
                <p className="text-xs text-[#a0a8b8]">Requires acknowledgment</p>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-[#a0a8b8] mb-1 block">Set hard loss limit</label>
                  <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748b] text-xs">$</span>
                    <input type="number" value={lossLimit} onChange={e => setLossLimit(e.target.value)} placeholder="50"
                      className="w-full h-9 pl-6 pr-3 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-xs focus:outline-none" data-testid="proceed-loss-limit" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#a0a8b8] mb-1 block">Type "I understand" to proceed</label>
                  <input type="text" value={ackText} onChange={e => setAckText(e.target.value)} placeholder="I understand"
                    className="w-full h-9 px-3 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-xs focus:outline-none" data-testid="proceed-ack-input" />
                </div>
                <button onClick={() => { setCountdown(5); setTimeout(handleProceed, 5000); }} disabled={ackText.toLowerCase() !== 'i understand' || !lossLimit || countdown > 0}
                  className="w-full h-9 rounded-lg bg-[#ef4444] text-white text-xs font-medium disabled:opacity-50" data-testid="proceed-btn">
                  {countdown > 0 ? `Wait ${countdown}s...` : 'Proceed to Deposit'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 6: Post-Decision */}
      {step === 6 && decision && (
        <div className="animate-slide-up max-w-lg mx-auto" data-testid="step-6">
          <div className={`ciq-card text-center ${decision.decision === 'waited' ? 'border-[#10b981]/30 bg-[#10b981]/5' : 'border-[#f59e0b]/30'}`}>
            {decision.decision === 'waited' ? (
              <>
                <Check className="h-12 w-12 text-[#10b981] mx-auto mb-3" />
                <h2 className="text-2xl font-bold text-white mb-2">Wise Choice!</h2>
                <p className="text-sm text-[#a0a8b8] mb-4">{decision.message}</p>
                {decision.badge && <p className="text-xs text-[#f59e0b] mb-4">Badge earned: {decision.badge}</p>}
                <div className="space-y-2 text-left mb-4">
                  {decision.next_steps?.map((s, i) => (
                    <p key={i} className="text-xs text-[#a0a8b8] flex items-start gap-2"><Check className="h-3 w-3 text-[#10b981] shrink-0 mt-0.5" />{s}</p>
                  ))}
                </div>
              </>
            ) : (
              <>
                <AlertTriangle className="h-12 w-12 text-[#f59e0b] mx-auto mb-3" />
                <h2 className="text-xl font-bold text-white mb-2">Please Gamble Responsibly</h2>
                <p className="text-sm text-[#a0a8b8] mb-4">{decision.message}</p>
                <div className="space-y-2 text-left mb-4">
                  {decision.reminders?.map((r, i) => (
                    <p key={i} className="text-xs text-[#a0a8b8] flex items-start gap-2"><AlertTriangle className="h-3 w-3 text-[#f59e0b] shrink-0 mt-0.5" />{r}</p>
                  ))}
                </div>
              </>
            )}
            <div className="flex gap-3 justify-center">
              <button onClick={reset} className="h-10 px-5 rounded-lg bg-[#4d7cfe] text-white text-sm font-medium btn-lift">Done</button>
              <Link to="/emotional-wellness/alternatives" className="h-10 px-5 rounded-lg border border-white/10 text-[#a0a8b8] text-sm flex items-center gap-2 hover:bg-white/5">
                Try Alternatives <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* History Stats */}
      {history && history.stats.total_reflections > 0 && step === 1 && (
        <div className="ciq-card mt-8 max-w-lg">
          <h3 className="text-white font-semibold text-sm mb-3">Your Reflection History</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-[#1a1f2e]"><p className="text-xs text-[#64748b]">Total Reflections</p><p className="text-lg font-bold text-white">{history.stats.total_reflections}</p></div>
            <div className="p-3 rounded-lg bg-[#1a1f2e]"><p className="text-xs text-[#64748b]">Times Waited</p><p className="text-lg font-bold text-[#10b981]">{history.stats.times_waited} ({history.stats.wait_rate}%)</p></div>
            <div className="p-3 rounded-lg bg-[#1a1f2e]"><p className="text-xs text-[#64748b]">Times Deposited</p><p className="text-lg font-bold text-[#ef4444]">{history.stats.times_deposited}</p></div>
            <div className="p-3 rounded-lg bg-[#1a1f2e]"><p className="text-xs text-[#64748b]">Est. Saved</p><p className="text-lg font-bold text-[#f59e0b]">${history.stats.estimated_saved.toLocaleString()}</p></div>
          </div>
        </div>
      )}
    </div>
  );
}
